import { useEffect } from "react";
import "./ArogyaBeeLabsPrivacyPolicy.css";
import iconSrc from '../../assets/svg/arogyabee-labs-icon.png';

const ArogyaBeeLabsPrivacyPolicy = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "ArogyaBee Labs – Privacy Policy";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="policy-page">
      <div className="header-section">
        <img src={iconSrc} alt="ArogyaBee Labs" className="company-logo" />
        <p className="company-name">ArogyaBee Labs</p>
        <p className="owner-name">eDigitizeYourself (eHomosapiens)</p>
      </div>

      <h1>Privacy Policy</h1>
      <p>
        <strong>Last updated: June 4, 2026</strong>
      </p>
      <p>
        This Privacy Policy describes Our policies and procedures on the
        collection, use, and disclosure of Your information when You use the
        ArogyaBee Labs application and tells You about Your privacy rights and
        how the law protects You.
      </p>
      <p>
        We use Your Personal Data to provide and improve the Service. By using
        the Service, You agree to the collection and use of information in
        accordance with this Privacy Policy.
      </p>

      <h2>Interpretation and Definitions</h2>
      <h3>Interpretation</h3>
      <p>
        The words of which the initial letter is capitalized have meanings
        defined under the following conditions. The following definitions shall
        have the same meaning regardless of whether they appear in singular or
        in plural.
      </p>
      <h3>Definitions</h3>
      <p>For the purposes of this Privacy Policy:</p>
      <ul>
        <li>
          <p>
            <strong>Account</strong> means a unique account created for You to
            access our Service or parts of our Service.
          </p>
        </li>
        <li>
          <p>
            <strong>Application</strong> refers to ArogyaBee Labs, the
            diagnostic lab management software provided by the Company (package
            name: <code>com.ehs.arogyabeelabs</code>).
          </p>
        </li>
        <li>
          <p>
            <strong>Company</strong> (referred to as either &quot;the
            Company&quot;, &quot;We&quot;, &quot;Us&quot; or &quot;Our&quot; in
            this Agreement) refers to eHomoSapien Technologies Inc.
          </p>
        </li>
        <li>
          <p>
            <strong>Device</strong> means any device that can access the
            Service such as a smartphone, tablet, or computer.
          </p>
        </li>
        <li>
          <p>
            <strong>Personal Data</strong> is any information that relates to
            an identified or identifiable individual.
          </p>
        </li>
        <li>
          <p>
            <strong>Service</strong> refers to the ArogyaBee Labs Application.
          </p>
        </li>
        <li>
          <p>
            <strong>Service Provider</strong> means any natural or legal person
            who processes the data on behalf of the Company to facilitate the
            Service.
          </p>
        </li>
        <li>
          <p>
            <strong>Usage Data</strong> refers to data collected automatically,
            either generated by the use of the Service or from the Service
            infrastructure itself.
          </p>
        </li>
        <li>
          <p>
            <strong>You</strong> means the individual — such as a lab
            administrator, technician, or staff member — accessing or using the
            Service.
          </p>
        </li>
      </ul>

      <h2>About ArogyaBee Labs</h2>
      <p>
        ArogyaBee Labs is a <strong>diagnostic laboratory management
        application</strong> designed for lab owners and staff. It enables
        lab-side users to manage patient bookings, schedule sample collections,
        assign field technicians, upload diagnostic reports, and track lab
        operations in real time.
      </p>
      <p>
        This application is intended solely for <strong>registered diagnostic
        lab partners</strong> of the ArogyaBee platform. Patient-facing
        features are handled by the separate ArogyaBee patient application
        (package: <code>com.arogyabee.patient</code>).
      </p>

      <h2>Collecting and Using Your Personal Data</h2>
      <h3>Types of Data Collected</h3>
      <h4>Personal Data</h4>
      <p>
        While using Our Service, We may ask You to provide certain personally
        identifiable information that can be used to contact or identify You.
        This may include:
      </p>
      <ul>
        <li>
          <p>Full name</p>
        </li>
        <li>
          <p>Email address</p>
        </li>
        <li>
          <p>Mobile / phone number</p>
        </li>
        <li>
          <p>Role and designation within the lab</p>
        </li>
        <li>
          <p>Lab name, Lab ID, and lab operational status</p>
        </li>
        <li>
          <p>Address and geolocation data for sample collection routing</p>
        </li>
        <li>
          <p>Usage Data</p>
        </li>
      </ul>
      <h4>Patient Data (Processed on Behalf of Labs)</h4>
      <p>
        In the course of managing bookings and diagnostic reports, the
        Application processes patient-related information on behalf of the
        registered lab, including:
      </p>
      <ul>
        <li>Patient name, age, and gender</li>
        <li>Patient contact number</li>
        <li>Requested diagnostic tests and scheduled appointment slots</li>
        <li>Collection address and type (home or walk-in)</li>
        <li>Diagnostic report files uploaded by lab staff</li>
      </ul>
      <p>
        This patient data is processed strictly to fulfil the service the lab
        provides to its patients, under applicable healthcare data protection
        obligations. We do not sell, rent, or share patient health information
        with third parties for advertising or marketing purposes.
      </p>
      <h4>Usage Data</h4>
      <p>
        Usage Data is collected automatically when using the Service and may
        include information such as Your device&apos;s IP address, device type
        and model, operating system version, app version, screens visited,
        time spent, and crash diagnostics.
      </p>
      <h4>Information Collected While Using the Application</h4>
      <p>
        With Your prior permission, We may collect:
      </p>
      <ul>
        <li>
          <strong>Camera and photos:</strong> To allow lab staff to upload lab
          logo images, certification documents, or attach relevant files when
          setting up or updating the lab profile.
        </li>
        <li>
          <strong>Files and storage:</strong> To attach and upload diagnostic
          report documents (PDF, images) to patient booking records.
        </li>
        <li>
          <strong>Location:</strong> Approximate or precise device location may
          be used by field technicians for sample collection routing and
          real-time status updates.
        </li>
      </ul>
      <p>
        You can enable or disable access to this information at any time
        through Your device settings.
      </p>

      <h2>Device Permissions, Advertising Identifiers, and Third-Party Services</h2>
      <p>
        On Android, the Application may declare permissions that match how the
        app is built on Google Play (for example, network access, camera,
        storage, and location). We request sensitive permissions only when a
        feature requires them and explain the purpose when the system prompts You.
      </p>
      <p>
        The Application may use an advertising ID permission where required by
        the platform or integrated SDKs for measurement, fraud prevention, or
        attribution. If an SDK collects or uses the advertising ID, its
        processing is governed by that provider&apos;s policies as well as this
        Privacy Policy.
      </p>
      <p>
        We use third-party services that may collect information described in
        this Policy, including:
      </p>
      <ul>
        <li>
          <strong>Google Analytics for Firebase</strong> – for usage tracking
          and analytics.{" "}
          <a
            href="https://firebase.google.com/support/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Firebase Privacy Policy
          </a>
        </li>
        <li>
          <strong>Amazon Web Services (AWS)</strong> – for secure cloud data
          storage and infrastructure.{" "}
          <a
            href="https://aws.amazon.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            AWS Privacy Notice
          </a>
        </li>
        <li>
          <strong>Google Cloud</strong> – for cloud infrastructure and
          services.{" "}
          <a
            href="https://cloud.google.com/terms/cloud-privacy-notice"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Cloud Privacy Notice
          </a>
        </li>
      </ul>

      <h2>Health-Related Information</h2>
      <p>
        Patient diagnostic data processed through this Application (including
        test results, report files, and health information) is treated as
        highly sensitive Personal Health Information (PHI). We use it strictly
        to provide the lab management service, maintain records, comply with
        applicable law, and keep the platform secure. We explicitly do{" "}
        <strong>not</strong> sell, rent, or share health information with third
        parties for advertising, marketing, or data-brokerage purposes.
      </p>

      <h3>Use of Your Personal Data</h3>
      <p>The Company may use Personal Data for the following purposes:</p>
      <ul>
        <li>
          <p>
            <strong>To provide and maintain the Service</strong>, including
            managing lab bookings, patient records, and diagnostic reports.
          </p>
        </li>
        <li>
          <p>
            <strong>To manage Your Account</strong> and provide access to
            role-appropriate features within the lab management platform.
          </p>
        </li>
        <li>
          <p>
            <strong>To communicate with You</strong> via email, SMS, or
            push notifications regarding booking updates, report uploads,
            platform changes, and security alerts.
          </p>
        </li>
        <li>
          <p>
            <strong>To manage Your requests</strong> and respond to support
            queries and lab operational needs.
          </p>
        </li>
        <li>
          <p>
            <strong>For business transfers:</strong> We may use Your
            information to evaluate or conduct a merger, acquisition,
            restructuring, or similar transaction.
          </p>
        </li>
        <li>
          <p>
            <strong>For analytics and improvement:</strong> To understand usage
            patterns, identify issues, and improve the Service.
          </p>
        </li>
      </ul>
      <p>We may share Your personal information in the following situations:</p>
      <ul>
        <li>
          <strong>With Service Providers:</strong> To monitor and analyse the
          use of our Service and to contact You.
        </li>
        <li>
          <strong>For business transfers:</strong> In connection with a merger,
          sale, or acquisition.
        </li>
        <li>
          <strong>With Affiliates:</strong> Subject to their adherence to this
          Privacy Policy.
        </li>
        <li>
          <strong>With Your consent:</strong> For any other purpose with Your
          explicit consent.
        </li>
      </ul>

      <h3>Retention of Your Personal Data</h3>
      <p>
        The Company will retain Your Personal Data only for as long as is
        necessary for the purposes set out in this Privacy Policy, and to
        comply with applicable legal obligations, resolve disputes, and enforce
        our agreements.
      </p>
      <p>
        Upon Your request to delete Your account, We will securely delete or
        anonymise Your Personal Data within 30 days, unless a longer retention
        period is required or permitted by law.
      </p>

      <h3>Transfer of Your Personal Data</h3>
      <p>
        Your information, including Personal Data, may be processed at the
        Company&apos;s operating offices and in any other places where the
        parties involved in processing are located. Your consent to this
        Privacy Policy followed by Your submission of such information
        represents Your agreement to that transfer. We will take all steps
        reasonably necessary to ensure Your data is treated securely.
      </p>

      <h3>Delete Your Personal Data</h3>
      <p>
        You have the right to request deletion of Your Personal Data. You may
        sign in to Your account and visit the account settings section, or
        contact us directly. To submit a data deletion request, please visit{" "}
        <a
          href="https://arogyabee.edigitizeyourself.com/ehs/account-deletion-info"
          target="_blank"
          rel="noopener noreferrer"
        >
          our Data Deletion Request page
        </a>{" "}
        or contact us at{" "}
        <a href="mailto:user.admin@edigitizeyourself.com">
          user.admin@edigitizeyourself.com
        </a>
        .
      </p>
      <p>
        Please note that We may need to retain certain information when We have
        a legal obligation or lawful basis to do so.
      </p>

      <h3>Disclosure of Your Personal Data</h3>
      <h4>Third-Party Services and Analytics</h4>
      <p>
        We may use third-party Service Providers to monitor and analyse the use
        of our Service, handle payments, or provide cloud infrastructure. These
        third parties have access to Your Personal Data only to perform tasks
        on Our behalf and are obligated not to disclose or use it for any other
        purpose.
      </p>
      <h4>Business Transactions</h4>
      <p>
        If the Company is involved in a merger, acquisition or asset sale, Your
        Personal Data may be transferred with prior notice.
      </p>
      <h4>Law Enforcement</h4>
      <p>
        Under certain circumstances, the Company may be required to disclose
        Your Personal Data if required to do so by law or in response to valid
        requests by public authorities.
      </p>
      <h4>Other Legal Requirements</h4>
      <p>
        The Company may disclose Your Personal Data in the good faith belief
        that such action is necessary to comply with a legal obligation, protect
        the rights or property of the Company, prevent possible wrongdoing, or
        protect against legal liability.
      </p>

      <h3>Security of Your Personal Data</h3>
      <p>
        The security of Your Personal Data is important to Us. While We strive
        to use commercially acceptable means to protect Your Personal Data,
        including encryption in transit and at rest, no method of transmission
        over the Internet or method of electronic storage is 100% secure.
      </p>

      <h2>Children&apos;s Privacy</h2>
      <p>
        Our Service does not address anyone under the age of 18. We do not
        knowingly collect personally identifiable information from anyone under
        the age of 18. This application is intended for adult healthcare
        professionals managing lab operations. If You become aware that a minor
        has provided Us with Personal Data, please contact Us immediately.
      </p>

      <h2>Links to Other Websites</h2>
      <p>
        Our Service may contain links to other websites not operated by Us. We
        strongly advise You to review the Privacy Policy of every site You
        visit. We have no control over and assume no responsibility for the
        content, privacy policies, or practices of any third-party sites or
        services.
      </p>

      <h2>Changes to this Privacy Policy</h2>
      <p>
        We may update Our Privacy Policy from time to time. We will notify You
        of any changes by posting the new Privacy Policy on this page and
        updating the &quot;Last updated&quot; date at the top. Where changes
        are material, We will notify You via email and/or a prominent notice
        within the Service prior to the change becoming effective.
      </p>
      <p>
        You are advised to review this Privacy Policy periodically for any
        changes.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy, You can contact
        us:
      </p>
      <ul>
        <li>
          By email:{" "}
          <a href="mailto:user.admin@edigitizeyourself.com">
            user.admin@edigitizeyourself.com
          </a>
        </li>
      </ul>
    </div>
  );
};

export default ArogyaBeeLabsPrivacyPolicy;
